// Real fishing zones for the Junior Crew Zone Map: catch rates, species
// tables, and the numbers the zone fishing simulation runs on.

export type ZoneSpecies = {
  name: string;
  /** Scientific name of the real California species. */
  sci: string;
  emoji: string;
  /** Relative chance of being the fish on the line, 0-100. */
  share: number;
  size: string;
  keeper: string;
  points: number;
};

export type Zone = {
  id: string;
  name: string;
  emoji: string;
  tagline: string;
  difficulty: 1 | 2 | 3 | 4 | 5;
  /** Base chance per cast that something bites, 0-1. */
  catchRate: number;
  bestTide: string;
  bestTime: string;
  bestBait: string[];
  /** Fishing style from the Character Creator that gets a bonus here. */
  favoredStyle: "pier" | "surf" | "kayak" | "boat";
  species: ZoneSpecies[];
  hazards: string[];
  art: string;
  strip: string;
  /** Catch-rate bonus (%) carried into the games once this zone is mastered. */
  masteryBonus: number;
};

export const ZONES: Zone[] = [
  {
    id: "beach",
    name: "Surf Beach",
    emoji: "🏖️",
    tagline: "Rolling waves, sandy troughs, and fish that cruise the wash.",
    difficulty: 2,
    catchRate: 0.45,
    bestTide: "Two hours either side of high tide",
    bestTime: "Early morning and the last hour of light",
    bestBait: ["Sand crab", "Bloodworm", "Cut anchovy"],
    favoredStyle: "surf",
    species: [
      { name: "Barred Surfperch", sci: "Amphistichus argenteus", emoji: "🐟", share: 45, size: "8–13 in", keeper: "No minimum size · 10 surfperch per day, max 5 of one kind", points: 10 },
      { name: "Yellowfin Croaker", sci: "Umbrina roncador", emoji: "🐠", share: 25, size: "10–16 in", keeper: "No minimum size · 10 croakers per day", points: 15 },
      { name: "California Halibut", sci: "Paralichthys californicus", emoji: "🫓", share: 12, size: "16–30 in", keeper: "22 in minimum · 5 per day south of Point Sur", points: 40 },
      { name: "Corbina", sci: "Menticirrhus undulatus", emoji: "🐟", share: 10, size: "12–20 in", keeper: "No minimum size · only 2 California corbina per day", points: 30 },
      { name: "Leopard Shark", sci: "Triakis semifasciata", emoji: "🦈", share: 8, size: "24–48 in", keeper: "36 in minimum · 3 per day", points: 50 },
    ],
    hazards: ["Big shore-break waves", "Rip currents", "Stingrays — shuffle your feet"],
    art: "from-amber-300 to-sky-700",
    strip: "bg-amber-300",
    masteryBonus: 3,
  },
  {
    id: "pier",
    name: "Ocean Pier",
    emoji: "🎡",
    tagline: "No license needed on most public CA piers — the best place to start.",
    difficulty: 1,
    catchRate: 0.55,
    bestTide: "Moving water, incoming or outgoing",
    bestTime: "Dawn, dusk, and after dark under the lights",
    bestBait: ["Live anchovy", "Squid strips", "Small metal jigs"],
    favoredStyle: "pier",
    species: [
      { name: "Pacific Mackerel", sci: "Scomber japonicus", emoji: "🐟", share: 40, size: "10–16 in", keeper: "No minimum size · counts in the 20-fish daily finfish limit", points: 10 },
      { name: "Jacksmelt", sci: "Atherinopsis californiensis", emoji: "🐟", share: 20, size: "8–14 in", keeper: "No minimum size · counts in the 20-fish daily finfish limit", points: 8 },
      { name: "Pacific Bonito", sci: "Sarda chiliensis", emoji: "🐡", share: 15, size: "14–24 in", keeper: "24 in or 5 lb south of Point Conception · 10 per day", points: 35 },
      { name: "Sheephead", sci: "Semicossyphus pulcher", emoji: "🐠", share: 12, size: "12–20 in", keeper: "12 in minimum · 2 per day", points: 30 },
      { name: "Halibut", sci: "Paralichthys californicus", emoji: "🫓", share: 8, size: "16–30 in", keeper: "22 in minimum · 5 per day south of Point Sur", points: 40 },
      { name: "Thresher Shark", sci: "Alopias vulpinus", emoji: "🦈", share: 5, size: "Big!", keeper: "2 per day, no minimum size — release from piers, get an adult", points: 60 },
    ],
    hazards: ["Crowded rails — look before you cast", "Slippery decks", "Never lean over the rail"],
    art: "from-sky-400 to-blue-800",
    strip: "bg-sky-400",
    masteryBonus: 3,
  },
  {
    id: "dock",
    name: "Harbor Dock",
    emoji: "⚓",
    tagline: "Flat calm water, shady pilings, and fish that live on the structure.",
    difficulty: 1,
    catchRate: 0.6,
    bestTide: "Slack to gently moving",
    bestTime: "All day; night under dock lights is best",
    bestBait: ["Small shrimp pieces", "Mussel", "Tiny squid strips"],
    favoredStyle: "pier",
    species: [
      { name: "Shiner Perch", sci: "Cymatogaster aggregata", emoji: "🐟", share: 45, size: "3–6 in", keeper: "No minimum size · part of the 10-fish surfperch limit", points: 5 },
      { name: "Opaleye", sci: "Girella nigricans", emoji: "🐠", share: 20, size: "8–14 in", keeper: "No minimum size · 10 per day", points: 15 },
      { name: "Sheephead", sci: "Semicossyphus pulcher", emoji: "🐠", share: 15, size: "12–20 in", keeper: "12 in minimum · 2 per day", points: 30 },
      { name: "Spotted Bay Bass", sci: "Paralabrax maculatofasciatus", emoji: "🐟", share: 15, size: "10–15 in", keeper: "14 in minimum · 5 bass per day (all bass combined)", points: 25 },
      { name: "Bat Ray", sci: "Myliobatis californica", emoji: "🥏", share: 5, size: "Wide and strong", keeper: "No size or bag limit · release carefully, mind the barb", points: 45 },
    ],
    hazards: ["Ask permission at private docks", "Watch your footing on wet planks", "Boat traffic"],
    art: "from-cyan-400 to-teal-800",
    strip: "bg-cyan-400",
    masteryBonus: 2,
  },
  {
    id: "jetty",
    name: "Rock Jetty",
    emoji: "🪨",
    tagline: "Rough rock, moving current, and the biggest fish you can reach from shore.",
    difficulty: 4,
    catchRate: 0.4,
    bestTide: "Outgoing tide along the current edge",
    bestTime: "First and last light",
    bestBait: ["Whole squid", "Live sardine", "Swimbaits"],
    favoredStyle: "surf",
    species: [
      { name: "Calico Bass", sci: "Paralabrax clathratus", emoji: "🐟", share: 35, size: "12–20 in", keeper: "14 in minimum · 5 bass per day (all bass combined)", points: 25 },
      { name: "Sheephead", sci: "Semicossyphus pulcher", emoji: "🐠", share: 20, size: "12–22 in", keeper: "12 in minimum · 2 per day", points: 30 },
      { name: "Lingcod", sci: "Ophiodon elongatus", emoji: "🐊", share: 15, size: "22–36 in", keeper: "22 in minimum · 2 per day, season set by groundfish area", points: 50 },
      { name: "Bonito", sci: "Sarda chiliensis", emoji: "🐡", share: 20, size: "14–24 in", keeper: "24 in or 5 lb south of Point Conception · 10 per day", points: 35 },
      { name: "Rockfish", sci: "Sebastes spp.", emoji: "🐠", share: 10, size: "10–18 in", keeper: "No minimum size · 10 per day inside the 20-fish RCG limit", points: 20 },
    ],
    hazards: ["Slippery, uneven rocks", "Surprise waves over the rocks", "Life jacket required"],
    art: "from-slate-400 to-zinc-800",
    strip: "bg-slate-400",
    masteryBonus: 4,
  },
  {
    id: "bridge",
    name: "Channel Bridge",
    emoji: "🌉",
    tagline: "Deep water, hard current, and fish stacked in the shadow line.",
    difficulty: 3,
    catchRate: 0.42,
    bestTide: "The hour on either side of a tide change",
    bestTime: "Night, along the light-and-shadow edge",
    bestBait: ["Cut mackerel", "Live bait", "Heavy jigs"],
    favoredStyle: "pier",
    species: [
      { name: "Spotted Bay Bass", sci: "Paralabrax maculatofasciatus", emoji: "🐟", share: 35, size: "10–16 in", keeper: "14 in minimum · 5 bass per day (all bass combined)", points: 25 },
      { name: "Sand Bass", sci: "Paralabrax nebulifer", emoji: "🐟", share: 25, size: "12–18 in", keeper: "14 in minimum · 5 bass per day (all bass combined)", points: 25 },
      { name: "Halibut", sci: "Paralichthys californicus", emoji: "🫓", share: 20, size: "16–32 in", keeper: "22 in minimum · 5 per day south of Point Sur", points: 40 },
      { name: "Bat Ray", sci: "Myliobatis californica", emoji: "🥏", share: 12, size: "Very strong", keeper: "No size or bag limit · release carefully, mind the barb", points: 45 },
      { name: "Mackerel", sci: "Scomber japonicus", emoji: "🐟", share: 8, size: "10–16 in", keeper: "No minimum size · counts in the 20-fish daily finfish limit", points: 10 },
    ],
    hazards: ["Traffic above and beside you", "Never climb rails", "Strong current pulls your rig"],
    art: "from-indigo-400 to-blue-900",
    strip: "bg-indigo-400",
    masteryBonus: 3,
  },
  {
    id: "boat",
    name: "Offshore Boat",
    emoji: "🚤",
    tagline: "Open water, working birds, and kelp paddies holding the big ones.",
    difficulty: 5,
    catchRate: 0.5,
    bestTide: "Tide matters less — follow the bait and the temperature break",
    bestTime: "Grey light at sunrise",
    bestBait: ["Live sardine", "Flylined anchovy", "Surface iron"],
    favoredStyle: "boat",
    species: [
      { name: "Yellowtail", sci: "Seriola dorsalis", emoji: "🐟", share: 25, size: "24–40 in", keeper: "24 in minimum · 10 per day, max 5 under 24 in", points: 60 },
      { name: "Dorado (Mahi)", sci: "Coryphaena hippurus", emoji: "🐬", share: 15, size: "20–40 in", keeper: "No minimum size · 10 per day", points: 65 },
      { name: "Bluefin Tuna", sci: "Thunnus orientalis", emoji: "🐋", share: 10, size: "Huge", keeper: "No minimum size · 2 per day (limits change each year)", points: 90 },
      { name: "Rockfish", sci: "Sebastes spp.", emoji: "🐠", share: 30, size: "10–20 in", keeper: "No minimum size · 10 per day inside the 20-fish RCG limit", points: 20 },
      { name: "Barracuda", sci: "Sphyraena argentea", emoji: "🐍", share: 20, size: "24–36 in", keeper: "28 in minimum · 3 per day — sharp teeth, use pliers", points: 35 },
    ],
    hazards: ["Life jacket on, always", "Hold rails when the boat moves", "Listen to the captain first, fish second"],
    art: "from-blue-400 to-indigo-900",
    strip: "bg-blue-400",
    masteryBonus: 5,
  },
  {
    id: "kayak",
    name: "Kayak Flats",
    emoji: "🛶",
    tagline: "Quiet, sneaky, and close to fish that spook from shore.",
    difficulty: 4,
    catchRate: 0.48,
    bestTide: "Incoming tide over the flats",
    bestTime: "Calm mornings before the wind builds",
    bestBait: ["Soft plastic swimbaits", "Live smelt", "Small spoons"],
    favoredStyle: "kayak",
    species: [
      { name: "Calico Bass", sci: "Paralabrax clathratus", emoji: "🐟", share: 35, size: "12–20 in", keeper: "14 in minimum · 5 bass per day (all bass combined)", points: 25 },
      { name: "Halibut", sci: "Paralichthys californicus", emoji: "🫓", share: 25, size: "16–30 in", keeper: "22 in minimum · 5 per day south of Point Sur", points: 40 },
      { name: "Bonito", sci: "Sarda chiliensis", emoji: "🐡", share: 20, size: "14–24 in", keeper: "24 in or 5 lb south of Point Conception · 10 per day", points: 35 },
      { name: "White Seabass", sci: "Atractoscion nobilis", emoji: "🐟", share: 10, size: "28–50 in", keeper: "28 in minimum · 3 per day, only 1 per day Mar 15–Jun 15", points: 70 },
      { name: "Sand Bass", sci: "Paralabrax nebulifer", emoji: "🐟", share: 10, size: "12–18 in", keeper: "14 in minimum · 5 bass per day (all bass combined)", points: 25 },
    ],
    hazards: ["Life jacket always on", "Stay close to shore until you are experienced", "Carry a whistle and go with an adult"],
    art: "from-emerald-400 to-green-800",
    strip: "bg-emerald-400",
    masteryBonus: 4,
  },
  {
    id: "bay",
    name: "Bay & Grass Flat",
    emoji: "🌿",
    tagline: "Shallow, warm, and packed with baby fish and hungry ambushers.",
    difficulty: 2,
    catchRate: 0.52,
    bestTide: "Incoming water flooding the grass",
    bestTime: "Mid-morning as the flat warms",
    bestBait: ["Live shrimp", "Small grubs", "Ghost shrimp"],
    favoredStyle: "kayak",
    species: [
      { name: "Spotted Bay Bass", sci: "Paralabrax maculatofasciatus", emoji: "🐟", share: 40, size: "10–15 in", keeper: "14 in minimum · 5 bass per day (all bass combined)", points: 25 },
      { name: "Halibut", sci: "Paralichthys californicus", emoji: "🫓", share: 20, size: "16–28 in", keeper: "22 in minimum · 5 per day south of Point Sur", points: 40 },
      { name: "Yellowfin Croaker", sci: "Umbrina roncador", emoji: "🐠", share: 20, size: "10–16 in", keeper: "No minimum size · 10 croakers per day", points: 15 },
      { name: "Round Stingray", sci: "Urobatis halleri", emoji: "🥏", share: 12, size: "Small and flat", keeper: "No limits · release — barbed tail, get an adult", points: 20 },
      { name: "Sargo", sci: "Anisotremus davidsonii", emoji: "🐟", share: 8, size: "8–14 in", keeper: "No minimum size · 10 per day", points: 15 },
    ],
    hazards: ["Shuffle your feet for stingrays", "Wear shoes — shells and glass", "Soft mud can hold a boot"],
    art: "from-lime-300 to-emerald-800",
    strip: "bg-lime-300",
    masteryBonus: 3,
  },
];

export function zoneById(id: string): Zone | undefined {
  return ZONES.find((z) => z.id === id);
}

export type CastResult = {
  hit: boolean;
  species?: ZoneSpecies;
  inches?: number;
  points: number;
  message: string;
};

/**
 * One simulated cast. The player's chosen fishing style and any zone
 * bonus they have already earned nudge the odds, so the simulation is
 * personal to each character — and the points feed the games.
 */
export function simulateCast(zone: Zone, style: string, bonusPct: number): CastResult {
  const styleBonus = style === zone.favoredStyle ? 0.12 : 0;
  const chance = Math.min(0.92, zone.catchRate + styleBonus + bonusPct / 100);
  if (Math.random() > chance) {
    const misses = [
      "The bait came back clean — try a different depth.",
      "A tap, then nothing. Something is down there!",
      "Crab stole your bait. Re-bait and cast again.",
      "Too much slack — reel tight after the cast.",
    ];
    return { hit: false, points: 0, message: misses[Math.floor(Math.random() * misses.length)] };
  }
  const total = zone.species.reduce((s, x) => s + x.share, 0);
  let roll = Math.random() * total;
  let species = zone.species[0];
  for (const s of zone.species) {
    roll -= s.share;
    if (roll <= 0) {
      species = s;
      break;
    }
  }
  const inches = Math.round(8 + Math.random() * 24 + species.points / 6);
  const points = Math.round(species.points * (1 + bonusPct / 100));
  return {
    hit: true,
    species,
    inches,
    points,
    message: `Fish on! ${species.emoji} ${species.name}, about ${inches} inches.`,
  };
}
